import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, ChevronDown, Loader2 } from 'lucide-react';
import { Branch, Product } from '../types';
import { BRANCHES } from '../config/siteConfig';
import { WhatsAppMessageService } from '../utils/whatsappMessageService';
import { WhatsAppIcon } from './BrandLogos';
import { useCartStore } from '../hooks/useCartStore';
import { useToast } from '../context/ToastContext';
import { productService } from '../services/ProductService';

interface WhatsAppProductButtonProps {
    product: Product;
    currentBranch: Branch;
    isAvailable: boolean;
    className?: string;
    variant?: 'default' | 'large';
    mobileText?: string;
    selectedVariant?: { name: string; sku: string };
    buttonText?: string;
}

const WhatsAppProductButton: React.FC<WhatsAppProductButtonProps> = ({
    product,
    currentBranch,
    isAvailable,
    className = '',
    variant = 'default',
    mobileText,
    selectedVariant,
    buttonText: customButtonText
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isCheckingStock, setIsCheckingStock] = useState(false);
    const [expandedBranchId, setExpandedBranchId] = useState<string | null>(null);
    const { isDrawerOpen } = useCartStore();
    const { showToast } = useToast();
    const buttonText = customButtonText || (isAvailable ? 'Comprar Ahora' : 'Consultar Reingreso');
    const mode = isAvailable ? 'product-available' : 'product-unavailable';

    // Architect: Close this modal if the global inquiry drawer opens
    useEffect(() => {
        if (isDrawerOpen) {
            setIsOpen(false);
        }
    }, [isDrawerOpen]);

    // Block body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleBranchPhoneClick = (branch: Branch, phoneIndex: number) => {
        const phone = branch.phones[phoneIndex];
        const message = mode === 'product-available'
            ? WhatsAppMessageService.buildProductAvailableMessage(product, branch, selectedVariant?.name)
            : WhatsAppMessageService.buildProductUnavailableMessage(product, currentBranch, selectedVariant?.name);

        if (WhatsAppMessageService.openWhatsApp(phone, message)) {
            WhatsAppMessageService.trackWhatsAppClick({
                source: variant === 'large' ? 'product_modal' : 'product_card',
                branch: branch.name,
                phoneIndex,
                productId: product.id,
                productName: selectedVariant ? `${product.name} (${selectedVariant.name})` : product.name,
                phoneNumber: phone,
                messageType: mode === 'product-available' ? 'buy' : 'consult'
            });
            setIsOpen(false);
        }
    };

    const handleButtonClick = async () => {
        if (isCheckingStock) return;

        if (mode === 'product-available') {
            setIsCheckingStock(true);
            try {
                // Determine model ID if possible
                let modelId;
                if (selectedVariant?.name && product.variants) {
                    const variant = product.variants.find(v => v.color_name === selectedVariant.name);
                    // Just take the first model id of the variant for the check if no specific device model is passed.
                    // (Actually the checkRealtimeAvailability handles undefined modelId by checking if ANY model is in stock)
                    if (variant && variant.models && variant.models.length > 0) {
                        modelId = variant.models[0].id; // Optimization: we could be more specific but this suffices
                    }
                }

                const stillAvailable = await productService.checkRealtimeAvailability(product.id, undefined, modelId);

                if (!stillAvailable) {
                    showToast('¡Ups! Parece que alguien más acaba de comprar la última unidad. Se ha agotado.', 'error', 5000);
                    setIsOpen(false);
                    return;
                }
            } catch (error) {
                console.error("JIT check error:", error);
            } finally {
                setIsCheckingStock(false);
            }
        }

        if (BRANCHES.length === 1 && BRANCHES[0].phones.length === 1) {
            handleBranchPhoneClick(BRANCHES[0], 0);
        } else {
            setIsOpen(true);
        }
    };

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsOpen(false)}
                        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9998]"
                    />
                    <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl pointer-events-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6 border-b border-zinc-900 bg-zinc-950 flex items-center justify-between sticky top-0 z-10 rounded-t-lg">
                                <div>
                                    <h3 className="text-white font-black uppercase tracking-widest text-xs">Contactar por WhatsApp</h3>
                                    <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-tight mt-1">Conecta directamente con un asesor</p>
                                </div>
                                <button onClick={() => setIsOpen(false)} className="p-2 hover:bg-zinc-800 rounded-full transition-colors group">
                                    <X className="h-5 w-5 text-zinc-500 group-hover:text-white" />
                                </button>
                            </div>
                            <div className="p-4 max-h-[60vh] overflow-y-auto space-y-2">
                                {BRANCHES.length === 1 ? (
                                    <div className="space-y-2">
                                        {BRANCHES[0].phones.map((phone, index) => (
                                            <button
                                                key={index}
                                                onClick={() => handleBranchPhoneClick(BRANCHES[0], index)}
                                                className="w-full flex items-center justify-between gap-4 bg-zinc-950 hover:bg-[#25D366] border border-zinc-900 hover:border-[#25D366] group/btn transition-all px-4 py-4 rounded-xl"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="bg-[#25D366]/10 p-2 rounded-lg group-hover/btn:bg-black/10 transition-colors">
                                                        <WhatsAppIcon className="w-5 h-5 text-[#25D366] group-hover/btn:text-black" />
                                                    </div>
                                                    <span className="text-base font-mono font-bold text-zinc-300 group-hover/btn:text-black">
                                                        {phone}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tighter opacity-40 group-hover/btn:opacity-100 group-hover/btn:text-black transition-all">
                                                    <span>Escribir</span>
                                                    <MessageCircle className="h-4 w-4" />
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                ) : (
                                    BRANCHES.map((branch) => {
                                        const isExpanded = expandedBranchId === branch.id;
                                        return (
                                            <div key={branch.id} className="bg-zinc-950/50 border border-zinc-800/30 rounded-xl overflow-hidden hover:border-zinc-700 transition-all">
                                                <button
                                                    onClick={() => setExpandedBranchId(isExpanded ? null : branch.id)}
                                                    className="w-full flex items-center justify-between p-3.5 hover:bg-zinc-900 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-[#25D366] shadow-[0_0_8px_rgba(37,211,102,0.4)]"></div>
                                                        <span className="text-[11px] font-black text-white uppercase tracking-widest">{branch.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-[9px] font-bold text-zinc-500 uppercase">{branch.phones.length} {branch.phones.length === 1 ? 'tel' : 'tels'}</span>
                                                        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                                    </div>
                                                </button>

                                                <AnimatePresence>
                                                    {isExpanded && (
                                                        <motion.div
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: 'auto', opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                        >
                                                            <div className="p-3 pt-0 space-y-2 bg-zinc-900/30">
                                                                <div className="h-px bg-zinc-800/50 mx-1 mb-2"></div>
                                                                {branch.phones.map((phone, index) => (
                                                                    <button
                                                                        key={index}
                                                                        onClick={() => handleBranchPhoneClick(branch, index)}
                                                                        className="w-full flex items-center justify-between gap-4 bg-zinc-900/50 hover:bg-[#25D366] border border-zinc-800/30 hover:border-[#25D366] group/btn transition-all px-4 py-3 rounded-xl"
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="bg-[#25D366]/10 p-2 rounded-lg group-hover/btn:bg-black/10 transition-colors">
                                                                                <WhatsAppIcon className="w-4 h-4 text-[#25D366] group-hover/btn:text-black" />
                                                                            </div>
                                                                            <span className="text-sm font-mono font-bold text-zinc-300 group-hover/btn:text-black">
                                                                                {phone}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-tighter opacity-40 group-hover/btn:opacity-100 group-hover/btn:text-black transition-all">
                                                                            <span>Escribir</span>
                                                                            <MessageCircle className="h-3 w-3" />
                                                                        </div>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );

    return (
        <>
            <button
                onClick={handleButtonClick}
                className={`${className} relative overflow-hidden group`}
            >
                <div className="relative z-10 flex items-center justify-center gap-2 md:gap-3 w-full px-4 transform transition-transform group-active:scale-95">
                    <div className="flex-shrink-0 flex items-center justify-center">
                        <WhatsAppIcon className={`${variant === 'large' ? 'h-5 w-5 md:h-6 md:w-6' : 'h-4 w-4 md:h-5 md:w-5'} transition-transform group-hover:scale-110`} />
                    </div>
                    <span className={`leading-tight text-center sm:text-left flex flex-col justify-center font-black ${variant === 'large' ? 'text-[11px] sm:text-[13px] md:text-[15px]' : ''}`}>
                        {isCheckingStock ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin" /> Verificando stock...
                            </span>
                        ) : mobileText ? (
                            <>
                                <span className="hidden sm:inline whitespace-normal">{buttonText}</span>
                                <span className="sm:hidden">{mobileText}</span>
                            </>
                        ) : (
                            <span className="whitespace-normal">{buttonText}</span>
                        )}
                    </span>
                </div>
            </button>
            {typeof document !== 'undefined' && createPortal(modalContent, document.body)}
        </>
    );
};

export default WhatsAppProductButton;
